import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  TrendingUp as BestPriceIcon,
  Speed as BestDeliveryIcon,
  Compare as CompareIcon
} from '@mui/icons-material';
import { useNotification } from '../../../contexts/NotificationContext';
import { SupplierQuoteProgressTracker } from './SupplierQuoteProgressTracker';

// Types based on our API response structure
interface SupplierQuoteResponse {
  supplier_case_id: string;
  supplier_case_number: string;
  supplier_name: string;
  supplier_email: string;
  supplier_id: string;
  quote_id: string;
  position_number: number;
  part_name: string;
  part_number: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  delivery_days: number;
  supplier_notes: string;
  currency: string;
  quote_date: string;
  quote_valid_until: string;
  payment_terms: string;
  delivery_terms: string;
  warranty_terms: string;
  special_conditions: string;
  notes: string;
  total_quote_value: number;
  delivery_time_days: number;
  quote_status: string;
}

interface CustomerPosition {
  id: string;
  position_number: number;
  part_name: string;
  part_number: string;
  notes: string;
  quantity: number;
  type_specific_data: any;
}

interface PositionWithQuotes {
  customer_position: CustomerPosition;
  supplier_quotes: SupplierQuoteResponse[];
  best_quote?: SupplierQuoteResponse;
  selection_status: 'pending' | 'selected' | 'rejected';
  quotes_count: number;
}

// 🎯 NEW: Enhanced structures for Position × Type matrix view
interface TypeSpecificQuote extends SupplierQuoteResponse {
  quote_type: string; // 'carbide', 'tool', 'material', etc.
  type_specific_unit_price: number;
  type_specific_total_price: number;
}

interface PositionTypeBreakdown {
  position_number: number;
  position_id: string;
  part_name: string;
  quantity: number;
  types: {
    [typeName: string]: {
      type_name: string;
      supplier_quotes: TypeSpecificQuote[];
      best_quote?: TypeSpecificQuote;
      selected_quote?: TypeSpecificQuote;
      selection_status: 'pending' | 'selected' | 'rejected';
    };
  };
}

interface CompilationSummary {
  by_type: {
    [typeName: string]: {
      total_value: number;
      position_count: number;
      quote_count: number;
    };
  };
  by_position: {
    [positionNumber: string]: {
      total_value: number;
      type_count: number;
      quote_count: number;
    };
  };
  grand_total: number;
  completion_percentage: number;
}

interface CompilationData {
  customer_case_id: string;
  positions: PositionWithQuotes[];
  summary: {
    total_positions: number;
    positions_with_quotes: number;
    total_quotes: number;
  };
  // 🎯 NEW: Enhanced compilation view
  enhanced_breakdown?: {
    position_type_matrix: PositionTypeBreakdown[];
    compilation_summary: CompilationSummary;
  };
}

interface CompilationProgress {
  customer_case_id: string;
  compilation_status: 'pending' | 'in_progress' | 'completed';
  positions: {
    total: number;
    quoted: number;
    selected: number;
  };
  percentage: {
    quoted: number;
    selected: number;
  };
  timestamps: {
    started_at: string | null;
    completed_at: string | null;
  };
  is_ready_for_export: boolean;
  selections: Record<string, any>;
}

interface CustomerQuoteCompilationManagerProps {
  open: boolean;
  onClose: () => void;
  customerCaseId: string;
  onCompilationComplete?: (compiledData: CompilationData) => void;
  onWorkflowTransition?: (fromState: string, toState: string) => Promise<void>;
  currentWorkflowState?: string;
}

export const CustomerQuoteCompilationManager: React.FC<CustomerQuoteCompilationManagerProps> = ({
  open,
  onClose,
  customerCaseId,
  onCompilationComplete,
  onWorkflowTransition,
  currentWorkflowState = 'preprocessing_needed'
}) => {
  const [compilationData, setCompilationData] = useState<CompilationData | null>(null);
  const [progress, setProgress] = useState<CompilationProgress | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedQuoteForPosition, setSelectedQuoteForPosition] = useState<string | null>(null);
  const [showQuoteSelectionDialog, setShowQuoteSelectionDialog] = useState(false);
  const [currentPosition, setCurrentPosition] = useState<PositionWithQuotes | null>(null);
  const [finalizing, setFinalizing] = useState(false);
  
  // 🎯 NEW: Enhanced view state
  const [viewMode, setViewMode] = useState<'standard' | 'enhanced'>('enhanced');
  const [selectedPositionType, setSelectedPositionType] = useState<{
    positionId: string;
    typeName: string;
    quoteId: string;
  } | null>(null);
  
  const { showNotification } = useNotification();

  // 🎯 NEW: Transform standard compilation data into Position × Type matrix
  const createEnhancedBreakdown = (data: CompilationData): CompilationData => {
    if (!data.positions || data.positions.length === 0) {
      return data;
    }

    const positionTypeMatrix: PositionTypeBreakdown[] = [];
    const summaryByType: { [typeName: string]: { total_value: number; position_count: number; quote_count: number; } } = {};
    const summaryByPosition: { [positionNumber: string]: { total_value: number; type_count: number; quote_count: number; } } = {};
    let grandTotal = 0;

    data.positions.forEach(position => {
      const { customer_position, supplier_quotes } = position;
      const selectedTypes = customer_position.type_specific_data?.selected_types || [];
      
      if (selectedTypes.length === 0) {
        console.warn('🚫 [COMPILATION] Position has no selected types:', customer_position.position_number);
        return;
      }

      const positionBreakdown: PositionTypeBreakdown = {
        position_number: customer_position.position_number,
        position_id: customer_position.id,
        part_name: customer_position.part_name,
        quantity: customer_position.quantity,
        types: {}
      };

      let positionTotal = 0;
      let positionTypeCount = 0;

      selectedTypes.forEach((typeName: string) => {
        // Find supplier quotes that match this type (based on supplier case naming convention)
        const typeQuotes: TypeSpecificQuote[] = supplier_quotes
          .filter(quote => {
            // Match based on supplier case number patterns
            const caseNumber = quote.supplier_case_number;
            if (typeName === 'carbide' && caseNumber.includes('CRB-Q')) return true;
            if (typeName === 'tool' && caseNumber.includes('TOL-Q')) return true;
            if (typeName === 'material' && caseNumber.includes('MAT-Q')) return true;
            return false;
          })
          .map(quote => ({
            ...quote,
            quote_type: typeName,
            type_specific_unit_price: quote.unit_price,
            type_specific_total_price: quote.total_price
          }));

        if (typeQuotes.length > 0) {
          const bestQuote = typeQuotes.reduce((best, current) => 
            current.total_price < best.total_price ? current : best
          );

          positionBreakdown.types[typeName] = {
            type_name: typeName,
            supplier_quotes: typeQuotes,
            best_quote: bestQuote,
            selection_status: 'pending' // Will be updated based on progress data
          };

          // Calculate totals
          const typeTotal = bestQuote.type_specific_total_price;
          positionTotal += typeTotal;
          positionTypeCount++;

          // Update type summary
          if (!summaryByType[typeName]) {
            summaryByType[typeName] = { total_value: 0, position_count: 0, quote_count: 0 };
          }
          summaryByType[typeName].total_value += typeTotal;
          summaryByType[typeName].position_count++;
          summaryByType[typeName].quote_count += typeQuotes.length;

          console.log(`🎯 [COMPILATION] Position ${customer_position.position_number} ${typeName}: ${typeTotal} EUR (${typeQuotes.length} quotes)`);
        } else {
          console.warn(`⚠️ [COMPILATION] No quotes found for Position ${customer_position.position_number} type ${typeName}`);
        }
      });

      // Update position summary
      summaryByPosition[customer_position.position_number.toString()] = {
        total_value: positionTotal,
        type_count: positionTypeCount,
        quote_count: Object.values(positionBreakdown.types).reduce((acc, type) => acc + type.supplier_quotes.length, 0)
      };

      grandTotal += positionTotal;
      positionTypeMatrix.push(positionBreakdown);
    });

    const compilationSummary: CompilationSummary = {
      by_type: summaryByType,
      by_position: summaryByPosition,
      grand_total: grandTotal,
      completion_percentage: Object.keys(summaryByPosition).length > 0 ? 100 : 0 // Simplified for now
    };

    console.log('✅ [COMPILATION] Enhanced breakdown created:', {
      positions: positionTypeMatrix.length,
      typeBreakdowns: Object.keys(summaryByType),
      grandTotal,
      summaryByType,
      summaryByPosition
    });

    return {
      ...data,
      enhanced_breakdown: {
        position_type_matrix: positionTypeMatrix,
        compilation_summary: compilationSummary
      }
    };
  };

  // Load compilation data and progress
  useEffect(() => {
    if (open && customerCaseId) {
      loadCompilationData();
      loadProgress();
    }
  }, [open, customerCaseId]);

  const loadCompilationData = async () => {
    setLoading(true);
    try {
      console.log('🔄 [COMPILATION] Loading supplier responses for customer case:', customerCaseId);
      // 🔄 CONSOLIDATED: Using correct customer-quote endpoint
      const response = await fetch(`/api/v3/customer-quotes/${customerCaseId}/supplier-responses`);
      const result = await response.json();
      
      console.log('📥 [COMPILATION] Supplier responses result:', result);
      
      if (result.success) {
        console.log('✅ [COMPILATION] Successfully loaded compilation data:', result.data);
        
        // 🎯 NEW: Create enhanced breakdown with Position × Type matrix
        const enhancedData = createEnhancedBreakdown(result.data);
        setCompilationData(enhancedData);
        
        console.log('🎯 [COMPILATION] Enhanced breakdown applied:', enhancedData.enhanced_breakdown);
      } else {
        console.error('❌ [COMPILATION] Error in response:', result.message);
        showNotification(`Fehler beim Laden der Lieferantenangebote: ${result.message}`, 'error');
      }
    } catch (error) {
      console.error('❌ [COMPILATION] Error loading compilation data:', error);
      showNotification('Fehler beim Laden der Daten', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async () => {
    try {
      console.log('🔄 [COMPILATION] Loading progress for customer case:', customerCaseId);
      // Keep using original endpoint - this remains customer-specific orchestration
      const response = await fetch(`/api/v3/customer-quotes/${customerCaseId}/compilation-progress`);
      const result = await response.json();
      
      console.log('📊 [COMPILATION] Progress result:', result);
      
      if (result.success) {
        console.log('✅ [COMPILATION] Successfully loaded progress:', result.data);
        setProgress(result.data);
      } else {
        console.warn('⚠️ [COMPILATION] Progress not available:', result.message);
      }
    } catch (error) {
      console.error('❌ [COMPILATION] Error loading progress:', error);
    }
  };

  // 🎯 NEW: Handle position + type specific quote selection
  const handlePositionTypeSelection = (positionId: string, typeName: string, quoteId: string) => {
    setSelectedPositionType({ positionId, typeName, quoteId });
    setShowQuoteSelectionDialog(true);
  };

  const handleQuoteSelection = (position: PositionWithQuotes, quote: SupplierQuoteResponse) => {
    setCurrentPosition(position);
    setSelectedQuoteForPosition(quote.quote_id);
    setShowQuoteSelectionDialog(true);
  };

  const confirmQuoteSelection = async (selectionReason: string = 'manual') => {
    if (!currentPosition || !selectedQuoteForPosition) return;

    try {
      const selectedQuote = currentPosition.supplier_quotes.find(q => q.quote_id === selectedQuoteForPosition);
      if (!selectedQuote) return;

      const response = await fetch(`/api/v3/customer-quotes/${customerCaseId}/select-quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_position_id: currentPosition.customer_position.id,
          selected_quote_id: selectedQuote.quote_id,
          selected_supplier_case_id: selectedQuote.supplier_case_id,
          selection_reason: selectionReason,
          customer_markup_percentage: 0
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        showNotification('Angebot erfolgreich ausgewählt', 'success');
        setShowQuoteSelectionDialog(false);
        
        // Reload data to reflect changes
        await loadCompilationData();
        await loadProgress();
        
        // Check if compilation is complete
        if (result.data.progress.percentage === 100) {
          showNotification('Angebotszusammenstellung abgeschlossen!', 'success');
          onCompilationComplete?.(compilationData!);
        }
      } else {
        showNotification('Fehler beim Speichern der Auswahl', 'error');
      }
    } catch (error) {
      console.error('Error selecting quote:', error);
      showNotification('Fehler beim Speichern der Auswahl', 'error');
    }
  };

  // Finalize preprocessing: Generate summary and transition to waiting_for_customer_decision
  const handleFinalizePreprocessing = async () => {
    if (!progress?.is_ready_for_export) {
      showNotification('Bitte wählen Sie zunächst alle Angebote aus', 'warning');
      return;
    }

    setFinalizing(true);
    try {
      console.log('🔄 [PREPROCESSING] Finalizing preprocessing for customer case:', customerCaseId);
      
      // Generate preprocessing summary (no customer interaction, just internal summary)
      const summaryResponse = await fetch(`/api/v3/customer-quotes/${customerCaseId}/preprocessing-summary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          summary_notes: 'Preprocessing completed - all supplier quotes selected and compiled'
        }),
      });

      const summaryResult = await summaryResponse.json();
      
      if (!summaryResult.success) {
        throw new Error(summaryResult.message || 'Failed to generate preprocessing summary');
      }

      console.log('✅ [PREPROCESSING] Preprocessing summary generated:', summaryResult.data);

      // Transition to waiting_for_customer_decision
      if (onWorkflowTransition && currentWorkflowState !== 'waiting_for_customer_decision') {
        console.log('🔄 [PREPROCESSING] Transitioning workflow state...');
        await onWorkflowTransition(currentWorkflowState, 'waiting_for_customer_decision');
      }

      showNotification('Vorverarbeitung abgeschlossen - Bereit für Kundenentscheidung', 'success');
      onCompilationComplete?.(compilationData!);
      onClose();
      
    } catch (error) {
      console.error('❌ [PREPROCESSING] Error finalizing preprocessing:', error);
      showNotification('Fehler beim Abschließen der Vorverarbeitung', 'error');
    } finally {
      setFinalizing(false);
    }
  };

  // Removed customer interaction functions (PDF, email, order conversion)
  // These will be handled in the Customer Approval Manager instead

  const formatCurrency = (amount: number, currency: string = 'EUR'): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const getBestQuoteIndicator = (quote: SupplierQuoteResponse, position: PositionWithQuotes) => {
    if (position.best_quote && quote.quote_id === position.best_quote.quote_id) {
      return (
        <Chip
          icon={<BestPriceIcon />}
          label="Bester Preis"
          color="success"
          size="small"
          sx={{ mr: 1 }}
        />
      );
    }
    
    // Check for fastest delivery
    const fastestDelivery = Math.min(...position.supplier_quotes.map(q => q.delivery_days));
    if (quote.delivery_days === fastestDelivery) {
      return (
        <Chip
          icon={<BestDeliveryIcon />}
          label="Schnellste Lieferung"
          color="primary"
          size="small"
          sx={{ mr: 1 }}
        />
      );
    }

    return null;
  };

  const isQuoteSelected = (positionId: string, quoteId: string): boolean => {
    return progress?.selections[positionId]?.selected_quote_id === quoteId;
  };

  if (!open) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Lieferantenangebote zusammenstellen
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Progress Overview */}
          {progress && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Fortschritt der Angebotszusammenstellung
                  </Typography>
                  <Chip
                    label={progress.compilation_status.replace('_', ' ').toUpperCase()}
                    color={progress.compilation_status === 'completed' ? 'success' : 'primary'}
                  />
                </Box>

                <Grid container spacing={2} mb={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      Positionen gesamt
                    </Typography>
                    <Typography variant="h4">
                      {progress.positions.total}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      Mit Angeboten
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {progress.positions.quoted}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      Ausgewählt
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {progress.positions.selected}
                    </Typography>
                  </Grid>
                </Grid>

                <Box mb={1}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">
                      Auswahlfortschritt
                    </Typography>
                    <Typography variant="body2">
                      {progress.percentage.selected}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progress.percentage.selected}
                    color={progress.percentage.selected === 100 ? 'success' : 'primary'}
                  />
                </Box>

                {progress.is_ready_for_export && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <strong>Exportbereit!</strong> Alle Positionen haben ausgewählte Angebote.
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Loading State */}
          {loading && (
            <Box display="flex" justifyContent="center" p={3}>
              <LinearProgress sx={{ width: '100%' }} />
            </Box>
          )}

          {/* Enhanced Supplier Quote Progress Tracker */}
          {!loading && (
            <SupplierQuoteProgressTracker
              customerCaseId={customerCaseId}
              onRefresh={loadCompilationData}
              showActions={true}
            />
          )}

          {/* 🎯 NEW: Enhanced Position × Type Matrix View */}
          {compilationData?.enhanced_breakdown && viewMode === 'enhanced' && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">
                    Angebotszusammenstellung nach Position × Typ
                  </Typography>
                  <Box>
                    <Button
                      variant={viewMode === 'enhanced' ? 'contained' : 'outlined'}
                      onClick={() => setViewMode('enhanced')}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      Matrix-Ansicht
                    </Button>
                    <Button
                      variant={viewMode === 'enhanced' ? 'outlined' : 'contained'}
                      onClick={() => setViewMode('standard')}
                      size="small"
                    >
                      Standard-Ansicht
                    </Button>
                  </Box>
                </Box>

                {/* Summary Totals */}
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          Gesamtsumme nach Typ
                        </Typography>
                        {Object.entries(compilationData.enhanced_breakdown.compilation_summary.by_type).map(([typeName, summary]) => (
                          <Box key={typeName} display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {typeName === 'carbide' ? 'Hartmetall' : 
                               typeName === 'tool' ? 'Werkzeug' : 
                               typeName === 'material' ? 'Material' : typeName}:
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(summary.total_value)}
                            </Typography>
                          </Box>
                        ))}
                        <Divider sx={{ my: 1 }} />
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="subtitle2">
                            Gesamtsumme:
                          </Typography>
                          <Typography variant="subtitle2" fontWeight="bold" color="primary">
                            {formatCurrency(compilationData.enhanced_breakdown.compilation_summary.grand_total)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          Gesamtsumme nach Position
                        </Typography>
                        {Object.entries(compilationData.enhanced_breakdown.compilation_summary.by_position).map(([positionNum, summary]) => (
                          <Box key={positionNum} display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2">
                              Position {positionNum}:
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(summary.total_value)}
                            </Typography>
                          </Box>
                        ))}
                        <Divider sx={{ my: 1 }} />
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="subtitle2">
                            Gesamtsumme:
                          </Typography>
                          <Typography variant="subtitle2" fontWeight="bold" color="primary">
                            {formatCurrency(compilationData.enhanced_breakdown.compilation_summary.grand_total)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Position × Type Matrix Table */}
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Position</strong></TableCell>
                        <TableCell><strong>Teilename</strong></TableCell>
                        <TableCell><strong>Menge</strong></TableCell>
                        <TableCell><strong>Typ</strong></TableCell>
                        <TableCell><strong>Lieferant</strong></TableCell>
                        <TableCell><strong>Einzelpreis</strong></TableCell>
                        <TableCell><strong>Gesamtpreis</strong></TableCell>
                        <TableCell><strong>Lieferzeit</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Aktionen</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {compilationData.enhanced_breakdown.position_type_matrix.map((position) => (
                        Object.entries(position.types).map(([typeName, typeData], typeIndex) => {
                          const bestQuote = typeData.best_quote;
                          if (!bestQuote) return null;

                          return (
                            <TableRow key={`${position.position_id}-${typeName}`}>
                              {typeIndex === 0 && (
                                <TableCell rowSpan={Object.keys(position.types).length}>
                                  <Typography variant="body2" fontWeight="bold">
                                    {position.position_number}
                                  </Typography>
                                </TableCell>
                              )}
                              {typeIndex === 0 && (
                                <TableCell rowSpan={Object.keys(position.types).length}>
                                  <Typography variant="body2">
                                    {position.part_name}
                                  </Typography>
                                </TableCell>
                              )}
                              {typeIndex === 0 && (
                                <TableCell rowSpan={Object.keys(position.types).length}>
                                  <Typography variant="body2">
                                    {position.quantity}
                                  </Typography>
                                </TableCell>
                              )}
                              <TableCell>
                                <Chip
                                  label={typeName === 'carbide' ? 'Hartmetall' : 
                                        typeName === 'tool' ? 'Werkzeug' : 
                                        typeName === 'material' ? 'Material' : typeName}
                                  color={typeName === 'carbide' ? 'secondary' : 
                                        typeName === 'tool' ? 'primary' : 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {bestQuote.supplier_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {bestQuote.supplier_case_number}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatCurrency(bestQuote.type_specific_unit_price)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatCurrency(bestQuote.type_specific_total_price)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {bestQuote.delivery_days} Tage
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={typeData.selection_status === 'selected' ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                                  label={typeData.selection_status === 'selected' ? 'Ausgewählt' : 'Ausstehend'}
                                  color={typeData.selection_status === 'selected' ? 'success' : 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {typeData.selection_status !== 'selected' && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handlePositionTypeSelection(position.position_id, typeName, bestQuote.quote_id)}
                                  >
                                    Auswählen
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Standard View (existing) */}
          {viewMode === 'standard' && (
            <>
              {/* Enhanced Supplier Quote Progress Tracker */}
              {!loading && (
                <SupplierQuoteProgressTracker
                  customerCaseId={customerCaseId}
                  onRefresh={loadCompilationData}
                  showActions={true}
                />
              )}
            </>
          )}

          {/* No Data State */}
          {compilationData && (!compilationData.positions || compilationData.positions.length === 0) && !loading && (
            <Alert severity="info">
              Keine Lieferantenangebote für diese Kundenanfrage gefunden.
              Bitte stellen Sie sicher, dass Lieferantenanfragen gesendet wurden und Angebote eingegangen sind.
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>
            Schließen
          </Button>
          {progress?.is_ready_for_export && currentWorkflowState === 'preprocessing_needed' && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleFinalizePreprocessing}
              disabled={finalizing}
            >
              {finalizing ? 'Wird abgeschlossen...' : 'Vorverarbeitung abschließen'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Quote Selection Confirmation Dialog */}
      <Dialog
        open={showQuoteSelectionDialog}
        onClose={() => setShowQuoteSelectionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Angebot auswählen
        </DialogTitle>
        <DialogContent>
          {currentPosition && selectedQuoteForPosition && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Position {currentPosition.customer_position.position_number}: {currentPosition.customer_position.part_name}
              </Typography>
              
              {(() => {
                const selectedQuote = currentPosition.supplier_quotes.find(q => q.quote_id === selectedQuoteForPosition);
                if (!selectedQuote) return null;
                
                return (
                  <Card variant="outlined" sx={{ mt: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Ausgewähltes Angebot
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Lieferant:
                          </Typography>
                          <Typography variant="body2">
                            {selectedQuote.supplier_name}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Gesamtpreis:
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(selectedQuote.total_price, selectedQuote.currency)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Lieferzeit:
                          </Typography>
                          <Typography variant="body2">
                            {selectedQuote.delivery_days} Tage
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Gültig bis:
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(selectedQuote.quote_valid_until)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })()}
              
              <Alert severity="info" sx={{ mt: 2 }}>
                Möchten Sie dieses Angebot für Position {currentPosition.customer_position.position_number} auswählen?
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowQuoteSelectionDialog(false)}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={() => confirmQuoteSelection('manual')}
          >
            Angebot auswählen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
